import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の情報セキュリティ指針（route=/standards/infosec）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler/become や /standards/crisis と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
//   協会と執事とお客様の間で双方向の守秘義務契約を結ぶ
// 創作した年号・数値・実績・漏えい件数・具体的な製品名は書かない

export default function StandardsInfosecPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/infosec']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の情報セキュリティ指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const deviceRules = [
    {
      en: 'Separation',
      ja: '端末の分離',
      text: '業務で扱う連絡・名簿・予定・邸内の詳細は、あらかじめ定められた業務用の環境でのみ扱います。私物の端末へ業務情報を移さないことが、最初の境界線です。'
    },
    {
      en: 'Accounts',
      ja: 'アカウントの管理',
      text: '共有が必要な場合も、個人の私的アカウントへ転送して済ませません。発行・権限・失効の流れを家ごと・現場ごとに揃え、使わなくなった権限は残しません。'
    },
    {
      en: 'Screen',
      ja: '画面と周囲',
      text: '移動中や共用空間では、画面ののぞき込みと音声の漏れを前提に扱います。ロック、画面の向き、話し声の大きさは、場所に応じて静かに調整します。'
    },
    {
      en: 'Storage',
      ja: '保存場所の限定',
      text: '一時的なメモも、散在させれば管理対象になります。保存してよい場所と、残してはならない場所を平時に決め、終業時に残件を片付けます。'
    }
  ];

  const keyAxes = [
    {
      title: '鍵と複製',
      text: '預かった鍵の本数・用途・返却条件を記録し、私的な複製や第三者への一時貸しを行いません。所在が曖昧になった時点で、隠さず報告します。'
    },
    {
      title: '暗証番号とコード',
      text: '入退室・金庫・警報などに関わる番号は、口頭の雑談や私的なメモアプリへ散在させません。知る必要のある範囲に限り、更新時は旧情報を破棄します。'
    },
    {
      title: '入退室の記録',
      text: '誰が・いつ・どの入口を用いたかという情報は、警備とプライバシーの両面を持ちます。残す目的と保管期間を決め、目的外の閲覧や持ち出しを避けます。'
    }
  ];

  const mediaLimits = [
    {
      no: '01',
      title: '撮影の前提',
      text: '邸内・同行先・関係者の姿や持ち物を、許可なく写真や動画に残しません。記録が業務上必要な場合も、目的・範囲・保管先を先に合意します。'
    },
    {
      no: '02',
      title: '録音の境界',
      text: '会話の録音は、相手の了解と目的の明確さが前提です。備忘のための独断録音や、私的な端末への保存は、信頼を損なう行為として避けます。'
    },
    {
      no: '03',
      title: '共有と二次利用',
      text: '正当に取得した記録であっても、学習用・私的なポートフォリオ・外部への説明資料へ転用しません。不要になった時点で、復元しにくい方法で破棄します。'
    },
    {
      no: '04',
      title: '背景に映る情報',
      text: '意図しない映り込み——郵便物、画面、名札、車両、住所表示——にも注意します。写ってしまった場合は、公開や転送の前に止める判断が先です。'
    }
  ];

  const channelGuides = [
    {
      label: '経路の選定',
      lines: [
        '内容の機密度に応じて、使う手段を段階的に選ぶ',
        '緊急度が高くても、私的な連絡網へ無秩序に流さない',
        '宛先・CC・転送の範囲を、必要最小限に保つ'
      ]
    },
    {
      label: '言葉の粒度',
      lines: [
        '固有名詞や住所の詳細を、公開に近い経路へ書かない',
        '事実と推測を混ぜず、短い文で要件を渡す',
        '感情的な評価や憶測で本文を膨らませない'
      ]
    },
    {
      label: '終了後の整理',
      lines: [
        '一時的なやり取りに残った添付や下書きを片付ける',
        '誤送信に気づいたら、隠さず直ちに初動へ移る',
        '同じ内容を複数経路へ重複して残さない'
      ]
    }
  ];

  const documentFlows = [
    {
      en: 'Keep',
      ja: '保管',
      text: '紙もデータも、閲覧できる人と保管場所を限定します。机上に出しっぱなしにせず、見出しだけで内容が推測できるラベルの付け方にも配慮します。'
    },
    {
      en: 'Move',
      ja: '持ち出し',
      text: '邸外へ持ち出す必要がある場合は、目的と期間を明確にし、移動中の紛失を想定した携行方法を選びます。カフェや車内での長時間の展開は避けます。'
    },
    {
      en: 'Dispose',
      ja: '廃棄',
      text: '役割を終えた書類は、判読できる状態のまま捨てません。データの削除も、ゴミ箱に入れただけで終わらせず、共有範囲から消えたことを確認します。'
    }
  ];

  const exitSteps = [
    {
      no: '01',
      title: '返却',
      text: '鍵・カード・書類・貸与端末・備品など、形のある預かり物を一覧で返し、受領の確認を残します。'
    },
    {
      no: '02',
      title: '削除と失効',
      text: '手元に残った複製・写真・メモ・アカウント権限を削除し、共有フォルダや連絡網からの離脱を確認します。'
    },
    {
      no: '03',
      title: '申し送り',
      text: '継続中の案件と、まだ閉じられていない情報の所在を、後任と家側へ短い言葉で渡します。口頭だけに依存しません。'
    },
    {
      no: '04',
      title: '以後の沈黙',
      text: '退任後も、在任中に知った事柄を話題や実績紹介へ使いません。守秘は在任期間で終わらない約束です。'
    }
  ];

  const incidentFirst = [
    '気づいた事実を、時刻とともに短く書き留める',
    '推測で範囲を断定せず、定められた連絡先へ直ちに共有する',
    '拡散を止めるため、追加の転送や独自調査を急がない',
    '関係者への説明は、指示された範囲と順序に従う',
    '再発防止の論点は、収束後の振り返りで手順に戻す'
  ];

  const faqItems = [
    {
      q: '私物のスマートフォンで、短い予定確認だけ行うのは問題ですか？',
      a: '業務情報を私物端末で扱わないことが本指針の原則です。短い確認であっても、履歴・通知・スクリーンショット・バックアップを通じて情報が残ります。あらかじめ認められた業務用の環境を用い、例外が必要な場合は家ごとの取り決めと権限の範囲を文書で確認してください。'
    },
    {
      q: '退任後に、在任中の経験を一般的な事例として話してもよいですか？',
      a: '個別の家・人物・場所が特定できる要素を除いても、関係者が自身のことだと感じる詳細は話しません。守秘義務は協会・執事・お客様のあいだで双方向に結ばれる約束であり、退任後も続きます。学びとして共有する場合は、識別できない粒度まで抽象化し、必要なら協会側の教材化の手続きを経ます。'
    },
    {
      q: '情報の事故が疑われるとき、現場で最初にすべきことは何ですか？',
      a: '事実のメモと、定められた連絡系統への報告です。独断で関係者へ広く連絡したり、証拠を消すような操作をしたりしません。被害の拡大を止める措置と、正確な初動報告が先です。危機全般の優先順位は危機管理指針と併せて確認し、情報面の節度は本指針に沿って保ちます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の情報セキュリティ指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の情報セキュリティ指針。基準化の理由、端末とアカウント、鍵と入退室、写真と録音、連絡手段、書類の保管と廃棄、退任時の返却、事故が疑われるときの初動。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/infosec'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の情報セキュリティ指針', item: 'https://japanbutlerassociation.com/standards/infosec' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/gen2/std2-infosec-document-protection.jpg"
          alt="機密書類を鍵付き保管庫へ収納し、情報を保護する執事"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Information Security Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の情報セキュリティ指針</span>
              <span className="block text-[#d7b779]">信頼を守る取り扱いの基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める情報取り扱いの基準として、端末とアカウント、鍵と入退室、写真と録音、連絡手段、書類の保管と廃棄、退任時の返却、事故が疑われるときの初動を示します。情報を守ることは、技術の誇示ではなく、依頼者の生活と名誉を静かに守るための職能です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化の理由 — 中央寄せリード＋横帯引用（crisisの左番号帯とは別） */}
          <section>
            <div className="max-w-3xl mx-auto text-center mb-10">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                I. Why Protect by Standards
              </span>
              <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                情報を<span className="text-[#a38e65]">基準で守る理由</span>
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] text-left sm:text-center">
                執事の仕事は、予定・好み・家族関係・資産に関わる手がかり・来客の顔ぶれなど、外へ出せない事柄のそばに立ちます。善意と記憶だけに頼ると、持ち出す範囲も残す期間も人ごとに揺れます。基準化の目的は、監視を強めることではなく、誰が担当しても同じ節度で扱える座標を共有することにあります。
              </p>
            </div>
            <div className="bg-[#fbfaf7] border border-[#e2ddd2] px-6 sm:px-10 py-8 sm:py-10 mb-8">
              <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-center">
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                  <p>
                    当協会は、Associate・Professional・Masterの三段階と専門資格を通じて判断力を育てると同時に、現場で迷わない情報取り扱いの骨子を本指針にまとめています。協会・執事・お客様のあいだでは双方向の守秘義務契約を結び、一方的な黙約ではなく、相互に範囲を確かめ合う関係を前提とします。
                  </p>
                  <p>
                    守秘の理念は
                    <a href="/standards/confidentiality" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">守秘の基準</a>
                    と、有事の初動は
                    <a href="/standards/crisis" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">危機管理指針</a>
                    と併せて参照し、基準全体の見取り図は
                    <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                    から俯瞰できます。情報セキュリティは、特別な日の点検項目ではなく、毎日の短い手つきの積み重ねです。
                  </p>
                </div>
                <img
              src="/images/gen2/std2-infosec-team-briefing.jpg"
              alt="情報取扱基準ブリーフィング"
                  className="w-full h-52 object-cover border border-[#a38e65]/30"
                />
              </div>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mx-auto">
              本指針は「何を手元に置かないか」「どの経路で伝えるか」「終わったあとに何を残さないか」を先に定めます。便利さより復元のしにくさ、速さより宛先の正確さを優先する——その単調さが、長い信頼を支えます。
            </p>
          </section>

          {/* S2. 端末とアカウント — 画像左＋四枚の細罫行（縦スタック型とは別） */}
          <section>
            <div className="grid lg:grid-cols-[300px_1fr] gap-10 items-start">
              <div className="space-y-5 lg:sticky lg:top-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56]">
                  II. Devices & Accounts
                </span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em]">
                  端末とアカウントの管理
                </h2>
                <img
                  src="/images/generated/jba-std-infosec-03.jpg"
                  alt="端末と記録の扱いを点検する執事"
                  className="w-full h-48 object-cover border border-[#a38e65]/30"
                />
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
                  私物端末で業務情報を扱わない——この一文を、例外の多さで溶かさないことが要点です。
                </p>
              </div>
              <div>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                  端末は、入口であると同時に、履歴が残る出口でもあります。業務用と私用の境が曖昧になると、通知のプレビュー、クラウドの自動同期、家族との共用といった日常の操作が、意図しない共有経路になります。本指針では、分離・権限・画面・保存場所の四つの軸で手つきを揃えます。
                </p>
                <div className="border-t border-[#1a2942]">
                  {deviceRules.map((item, idx) => (
                    <div
                      key={item.en}
                      className={`py-6 ${idx < deviceRules.length - 1 ? 'border-b border-[#e2ddd2]' : ''}`}
                    >
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                        <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em]">{item.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.ja}</h3>
                      </div>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* S3. 鍵・暗証番号・入退室 — 三列アイコンレスカード＋下帯 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Keys & Access
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              鍵・暗証番号・入退室情報の取り扱い
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              物理的な鍵も、数字のコードも、誰が通ったかという記録も、いずれも「入れる権限」そのものです。便利さのために複製やメモを増やすほど、管理の輪郭はぼやけます。預かり・更新・返却のたびに、残っている情報の所在を数え直します。
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {keyAxes.map((item) => (
                <div
                  key={item.title}
                  className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7 flex flex-col"
                >
                  <div className="w-8 h-px bg-[#a38e65] mb-5"></div>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9] mt-auto">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm shrink-0">
                HOLD · LIMIT · RETURN
              </span>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.9]">
                預かったら範囲を限る。役目が終わったら返す。所在が曖昧な瞬間を、個人の discreet な努力で埋めないことが、現場の安全につながります。
              </p>
            </div>
          </section>

          {/* S4. 写真と録音 — 紺地パネル内の四分割（crisisの平時訓練と同色でも中身の型を変える） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Image & Sound
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              写真と録音の禁止範囲
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-0 border border-[#e2ddd2] mb-8">
              <div className="grid sm:grid-cols-2">
                {mediaLimits.map((item) => (
                  <div
                    key={item.no}
                    className="px-6 sm:px-7 py-6 border-b sm:border-r border-[#e2ddd2] odd:sm:border-r even:sm:border-r-0"
                  >
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl block mb-2">{item.no}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#1a2942] p-7 sm:p-8 flex flex-col justify-between min-h-[280px]">
                <div>
                  <span className="block font-['Playfair_Display',serif] text-[#d7b779] text-sm tracking-[0.12em] mb-4">
                    Principle
                  </span>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.95]">
                    記録媒体は、残した瞬間から管理責任が発生します。美しさや学習意欲があっても、許可と目的のない撮影・録音は行いません。映り込みに気づいたら、公開や転送の前に止める——その一手が信頼を守ります。
                  </p>
                </div>
                <img
                  src="/images/generated/jba-std-infosec-04.jpg"
                  alt="写真と録音の境界を研修で確認する基礎的な学びの場"
                  className="w-full h-36 object-cover border border-[#a38e65]/35 mt-6"
                />
              </div>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              業務上どうしても記録が必要な場面では、何を・どこまで・どこに保管し・いつ破棄するかを先に合意します。私的な端末のアルバムや、外部の自動バックアップへ流れ込まない経路を選び、目的が終わった記録は速やかに閉じます。
            </p>
          </section>

          {/* S5. 連絡手段 — 三列ガイド＋右テキストなしの横並び説明 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Choosing Channels
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              連絡手段の選び方
            </h2>
            <div className="bg-[#fbfaf7] border-l-2 border-[#a38e65] px-6 sm:px-8 py-6 mb-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                速さだけを基準に経路を選ぶと、機密度の高い内容が、履歴の残る私的な連絡網へ流れます。内容の重さ、相手の人数、添付の有無、緊急度を見て、使う手段を一段ずつ選び直すことが職能です。便利な一経路へすべてを集約しない——その分散と抑制が、事故の芽を小さくします。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-10">
              {channelGuides.map((block) => (
                <div key={block.label} className="border border-[#e2ddd2] bg-white p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#e2ddd2]">{block.label}</h3>
                  <ul className="space-y-3">
                    {block.lines.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-center">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  宛先の確認は、送信前の最後の礼儀です。同姓同名、自動補完、過去の転送履歴は、誤送信の典型的な温床になります。添付があるときは、ファイル名そのものに固有の情報が含まれていないかも見ます。
                </p>
                <p>
                  緊急時であっても、情報の粒度を落とさずに広範囲へ送ることは避けます。まず安全と取次を確保し、詳細は権限のある経路へ戻す——この順序は、
                  <a href="/standards/crisis" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">危機管理指針</a>
                  の初動とも矛盾しません。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-infosec-05.jpg"
                alt="連絡経路と宛先の確認を実務のなかで行う執事"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S6. 書類と記録 — 三分割トップ＋本文（保管・持出・廃棄） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Documents & Disposal
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              書類と記録の保管と廃棄
            </h2>
            <div className="grid md:grid-cols-3 gap-0 mb-10 border-t-2 border-[#1a2942]">
              {documentFlows.map((r) => (
                <div key={r.en} className="border-b md:border-b-0 md:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{r.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{r.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-start">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  保管は、鍵のかかる場所へ入れるだけで完了しません。誰が開けるのか、複製がどこにあるのか、期限を過ぎたものは誰が閉じるのか——ライフサイクルを一文で言える状態を目指します。デジタルの記録も同様で、共有フォルダの奥に眠る古いファイルは、忘れられたままの持ち出し口になり得ます。
                </p>
                <p>
                  廃棄は、見えなくすることと、復元しにくくすることの両方を含みます。シュレッダーや同等の処理、データの完全な削除、共有相手側のコピーの扱いまでを一連の作業として捉え、途中で「あとで」を挟まないことが大切です。判読できる紙を、一般の廃棄物へそのまま混ぜることはしません。
                </p>
              </div>
              <img
                    src="/images/gen2/jba-infosec-secure-storage-02.png"
                    alt="二重錠の書類金庫、無記名の施錠保管箱、鍵管理庫、投入口付き廃棄保管庫を備えた記録室"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S7. 退任時＋事故初動＋FAQ — 同一セクションに統合（8th h2前の7本目） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Exit & Incident
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              退任時の返却・削除と、事故が疑われるときの初動
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              情報の管理は、在任中の丁寧さだけでは閉じません。退任の手続きと、事故が疑われる瞬間の初動が、信頼の最終試験になります。どちらも個人の discreet な判断に委ねず、返却・削除・報告の型へ落とします。
            </p>

            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-0 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">RETURN → REVOKE → HANDOVER → SILENCE</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                退任は、情報の区切りでもある
              </span>
            </div>
            <div className="grid sm:grid-cols-2 border border-t-0 border-[#e2ddd2] bg-white mb-10">
              {exitSteps.map((item) => (
                <div key={item.no} className="px-6 sm:px-8 py-6 border-b sm:border-r border-[#e2ddd2] odd:sm:border-r even:sm:border-r-0">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl block mb-2">{item.no}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-start mb-12">
              <div>
                <h3 className="jba-display font-medium text-[#1a2942] text-xl mb-4">事故が疑われるときの初動</h3>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  誤送信、所在不明の媒体、権限の消し忘れ、許可のない撮影の発覚——疑いが生じた時点で、独断の調査や沈黙による様子見は選びません。事実を短い言葉で残し、定められた系統へ渡すことが、被害の拡大を止める最初の一手です。
                </p>
                <ul className="space-y-4">
                  {incidentFirst.map((line) => (
                    <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.85]">
                      <span className="text-[#a38e65] shrink-0">—</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8">
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] mb-5">
                  初動では、完璧な原因究明より、拡散の停止と正確な共有を優先します。現場の物を不用意に動かしたり、ログを消したりする行為は、その後の判断を困難にします。人命や施設の安全に関わる事態が重なる場合は、危機管理の優先順位に従いつつ、情報面の節度を同時に保ちます。
                </p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                  収束後は、責めの配分ではなく、手順・権限・経路のどこを直すかに落とし込みます。AssociateからMasterまで、同じ型で振り返ることで、個人の記憶が組織の判断へ変わっていきます。
                </p>
              </div>
            </div>

            <div className="border-t-2 border-[#1a2942]">
              <p className="font-['Playfair_Display',serif] uppercase tracking-[0.18em] text-[12px] text-[#8f7a56] pt-6 mb-4">
                Questions
              </p>
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. 関連導線 — 9th section, 8th h2 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Related Paths
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                情報セキュリティ指針は、単独で完結するものではありません。守秘の理念で扱う心を整え、危機管理で有事の初動を揃え、資格制度を通じて段階的に再現性を育てることで、日常の品位が続きます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/confidentiality" className={BTN08_ON_NAVY}>
                  守秘の基準
                </a>
                <a href="/standards/crisis" className={BTN08_ON_NAVY}>
                  危機管理指針
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
